import { convertFromRaw, EditorState } from "draft-js";
import { GetServerSidePropsContext, NextPageWithLayout } from "next";
import Head from "next/head";
import {
  CSSProperties,
  ReactElement,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Page, PageSecretFlag, ServiceViewType } from "@/apis/clients";
import { contentAccessLogApi } from "@/apis/content-access-log/contentAccessLogApi";
import { ApiResponseError, ApiValidationError } from "@/apis/errors";
import { pageApi } from "@/apis/page/pageApi";
import commonApi from "@/apis/common/commonApi";
import EditorDisplay, {
  ConnectedService,
  EditorDisplayOptions,
} from "@/components/editors/EditorDisplay";
import MainBlock from "@/components/editors/MainBlock";
import { AdvertisementFooter } from "@/components/organisms/footer/AdvertisementFooter";
import PageSecretPasswordForm, {
  PasswordParams,
} from "@/components/organisms/page-secret-password-from/PageSecretPasswordForm";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import pageSecretLoginStorage from "@/libs/page/pageSecretLoginStorage";
import { Device } from "@/models/editors/editor";
import { useLoading } from "@/states/loading/operations";
import { TemplatePart } from "@/states/page-editor/template-parts/types";
import { useFetchServiceForm } from "@/states/service-form/operations";
import { getDefaultOgpImage } from "@/utils/common";
import {
  convertFromRawRecursively,
  convertRecursion,
  isRawDraftContentState,
} from "@/utils/convert-page-json";
import { getTemplatePartArrayFromRaw, makeLinkDecorator } from "@/utils/draft";
import { findTopParts, type DraftBlock } from "@/utils/template/findTopParts";

import { OgpImageUrl } from "@/constants/ogp";
import { useLoadingUiState } from "@/states/loading/selectors";
import { useServiceFormAtom } from "@/states/service-form/selectors";
import { EventSeminarServiceInfo } from "@/states/service-form/types";
import { useFetchServiceComponentsByIds } from "@/states/service-top";
import { useFetchServiceComponents } from "@/states/service-top/operations";
import { GoogleTagManager } from "@next/third-parties/google";
import Script from "next/script";
import LogoImg from "/public/logo.svg";

// 通常テキストにリンクが貼られた時用のDecorator
const linkDecorator = makeLinkDecorator();

export interface State {
  isReady: boolean;
  isPreview: boolean;
  device: Device;
  pageId?: number | undefined;
  pageName: string | undefined;
  isOpenCreatedModal: boolean;
  pageStyleSettings: CSSProperties;
  editorState: EditorState;
  mainBlock?: TemplatePart;
  connectedService: ConnectedService | undefined;
}

interface GlobalCssProperties {
  backgroundColor: string;
  fontFamily: string;
  src: string;
  image_uuid: string;
}

interface Props {
  uuid: string;
  pageData: Page;
  serviceName?: string;
  ogp: {
    title: string;
    image: string;
    image_width: string;
    image_height: string;
  };
}

const PageIndex: NextPageWithLayout<Props> = ({
  uuid,
  pageData,
  serviceName,
  ogp,
}: Props) => {
  const [state, setState] = useState<State>({
    isReady: false,
    isPreview: false,
    device: Device.DESKTOP,
    pageName: undefined,
    pageId: undefined,
    isOpenCreatedModal: false,
    pageStyleSettings: {},
    editorState: EditorState.createEmpty(),
    connectedService: undefined,
  });

  const withLoading = useLoading();
  const loading = useLoadingUiState();

  const { handlePageError } = useApiResponseErrorHandler();
  const fetchServiceForm = useFetchServiceForm();
  const fetchServiceComponents = useFetchServiceComponents();
  const fetchServiceComponentsByIds = useFetchServiceComponentsByIds();
  const serviceFormAtom = useServiceFormAtom();
  const formDataService = serviceFormAtom.formData
    .service as EventSeminarServiceInfo;
  const [errors, setErrors] = useState<ApiValidationError | undefined>(
    undefined,
  );

  const [isShowPage, setIsShowPage] = useState<boolean>(false);
  const [page, setPage] = useState<Page>(pageData);

  const formUuid = useMemo(() => {
    return page?.form_uuid;
  }, [page]);
  const serviceViewType = useMemo(() => {
    return page?.view_type;
  }, [page]);

  const editorDisplayOptions: EditorDisplayOptions = useMemo(() => {
    const isSubscriptionService =
      serviceFormAtom.formData?.service?.type === "subscription";
    const isCalendarService =
      serviceFormAtom.formData?.service?.type === "calendar";

    const formPath =
      formUuid && serviceViewType === ServiceViewType.BUTTON && !isCalendarService
        ? isSubscriptionService
          ? `/subscription/${formUuid}/purchase?lpId=${uuid}`
          : `/form/${formUuid}?lpId=${uuid}&from=${ServiceViewType.BUTTON}`
        : "";

    const confirmPath =
      formUuid && !isSubscriptionService && !isCalendarService
        ? `/form/${formUuid}/confirm?lpId=${uuid}&from=${ServiceViewType.FORM}`
        : "";
    return {
      partsProps: {
        buttonLargeDoubleArrow: {
          formPath,
        },
        buttonServiceDefault: {
          formPath,
        },
        formCompany: {
          confirmPath,
        },
        ctaButtonService: {
          formPath,
          confirmPath,
        },
        serviceViewType: serviceViewType,
      },
    };
  }, [
    formUuid,
    serviceViewType,
    uuid,
    formDataService?.type,
    serviceFormAtom.inputs.service.detailIds,
  ]);

  const storage = useMemo(() => {
    return pageSecretLoginStorage(uuid);
  }, [uuid]);

  const handlePageSecretPassword = async ({ password }: PasswordParams) => {
    await withLoading(async () => {
      const res = await pageApi
        .postPageSecretPassword({
          uuid: uuid,
          postPageSecretPasswordRequest: {
            password,
          },
        })
        .catch((e) => {
          if (e instanceof ApiValidationError) {
            setErrors(e);
            return;
          }
        });
      if (res && res.data) {
        if (storage) {
          storage &&
            (await storage.set({
              uuid: uuid,
              isLogin: true,
            }));
        }
        setIsShowPage(true);
      }
    });
  };

  const isShowPageSecretPassword = (): boolean => {
    if (isShowPage) return false;
    return !!(
      page?.form_secret_flag && page.form_secret_flag === PageSecretFlag.SHOW
    );
  };

  useEffect(() => {
    const { mainBlock, draftContentState, globalCssProperties } =
      page.page_editor_content;
    // 紐づくサービスIDとテンプレートブロックkeyを取得
    const connectedService: ConnectedService | undefined =
      page.service_id !== undefined &&
      page.service_templatepart_id !== undefined
        ? {
            serviceId: page.service_id,
            templateBlockKey: page.service_templatepart_id,
          }
        : undefined;
    // JSONからContentStateを復元してEditorStateを初期化する
    const initialEditorState = EditorState.createWithContent(
      convertFromRawRecursively(draftContentState),
      linkDecorator,
    );
    // メインブロック
    const initialMainBlockState: TemplatePart | undefined = !mainBlock
      ? undefined
      : (convertRecursion(
          mainBlock,
          isRawDraftContentState,
          convertFromRaw,
        ) as TemplatePart);

    // ページ全体のスタイル設定
    const cssSettings = globalCssProperties as GlobalCssProperties;
    // 画像のみ、backgroundImageで保存できないため変換
    const pageCss: CSSProperties = {
      ...cssSettings,
      backgroundImage: cssSettings?.src
        ? cssSettings.src !== ""
          ? `url(${cssSettings.src})`
          : undefined
        : undefined,
    };

    setState((prev) => ({
      ...prev,
      editorState: initialEditorState,
      mainBlock: initialMainBlockState,
      connectedService: connectedService,
      pageStyleSettings: pageCss,
      isReady: true,
    }));
  }, [page]);

  useEffect(() => {
    const { draftContentState } = page.page_editor_content;
    const topParts = findTopParts(
      (draftContentState as unknown as { blocks?: DraftBlock[] })?.blocks,
    );

    // Fetch custom data for each custom part (async, non-blocking)
    const fetchCustomParts = async () => {
      for (const part of topParts) {
        if (
          part.mode === "custom" &&
          part.orderIds &&
          part.orderIds.length > 0
        ) {
          const idsToFetch =
            part.hiddenIds && part.hiddenIds.length > 0
              ? part.orderIds.filter((id) => !part.hiddenIds!.includes(id))
              : part.orderIds;

          if (idsToFetch.length > 0) {
            await fetchServiceComponentsByIds(part.partId, idsToFetch, uuid);
          }
        }
      }
    };

    void fetchCustomParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, uuid]); // Depends on page and uuid, NOT on defaultServices

  useEffect(() => {
    void (async () => {
      const loginStatus = await pageSecretLoginStorage(uuid).get();
      if (loginStatus?.uuid === uuid && loginStatus?.isLogin) {
        setIsShowPage(true);
      }
    })();
  }, [uuid]);

  useEffect(() => {
    if (formUuid === undefined) {
      return;
    }

    void (async () => {
      await withLoading(async () => {
        try {
          await fetchServiceForm(formUuid);
        } catch (e) {
          if (e instanceof ApiResponseError) {
            await handlePageError(e);
          }
          throw e;
        }
      });
    })();
  }, [
    formUuid,
    serviceViewType,
    fetchServiceForm,
    handlePageError,
    withLoading,
  ]);

  useEffect(() => {
    if (!uuid) return;
    void (async () => {
      await fetchServiceComponents(uuid);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  useEffect(() => {
    if (!uuid) return;

    let email: string | undefined;
    try {
      if (typeof window !== "undefined") {
        const customerFormData = localStorage.getItem("customer_form");
        if (customerFormData) {
          const parsed = JSON.parse(customerFormData) as { email?: string };
          email = parsed.email;
        }
      }
    } catch {
      // If parsing fails, email remains undefined
    }
    if (!email) return;

    void (async () => {
      try {
        // Get CSRF token first to ensure postLog succeeds
        await commonApi.getCsrfCookie();
        await contentAccessLogApi.postLog({
          pageUuid: uuid,
          email,
        });
      } catch {
        // Error is intentionally ignored to prevent breaking the LP page display
      }
    })();
  }, [uuid]);

  const headRenderer = () => {
    return (
      <Head>
        <title>{serviceName || ogp.title}</title>
        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:locale" content="ja_JP" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={ogp.title || serviceName} />
        <meta property="og:image" content={ogp.image || OgpImageUrl} />
        <meta property="og:image:width" content={ogp.image_width} />
        <meta property="og:image:height" content={ogp.image_height} />
        <meta property="og:description" content="" />
        <meta property="og:url" content="" />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogp.title || serviceName} />
        <meta name="twitter:image" content={ogp.image || OgpImageUrl} />
        <meta name="twitter:description" content="" />
        <meta property="twitter:domain" content="" />
        <meta property="twitter:url" content="" />
      </Head>
    );
  };

  useEffect(() => {
    const idMenu = "menu";
    if (!state.isReady || loading.status === "Loading") {
      return;
    }

    const shouldScrollToMenu =
      typeof window !== "undefined" && window.location.hash === `#${idMenu}`;
    if (!shouldScrollToMenu) {
      return;
    }

    const menuElement = document.getElementById(idMenu);

    if (menuElement) {
      menuElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [state.isReady, loading]);

  if (!state.isReady) return <>{headRenderer()}</>;

  return (
    <>
      {headRenderer()}
      {isShowPageSecretPassword() ? (
        <>
          <div
            className="py-2"
            style={{ boxShadow: "0px 1px 0px rgba(0, 0, 0, 0.25)" }}
          >
            <LogoImg
              height="26"
              title="Yoyacoo"
              className="block"
              style={{ margin: "auto" }}
            />
          </div>
          <PageSecretPasswordForm
            errors={errors}
            handlePageSecretSubmit={handlePageSecretPassword}
          />
        </>
      ) : (
        <div
          className="flex flex-col items-center min-w-full min-h-screen"
          style={state.pageStyleSettings}
        >
          <div className="px-4 space-y-10 w-full max-w-[920px] md:px-0">
            {state.mainBlock && (
              <MainBlock
                part={state.mainBlock}
                connectedService={state.connectedService}
                options={editorDisplayOptions}
                data-testid="main-block"
              />
            )}
            {state.editorState && (
              <EditorDisplay
                state={state.editorState}
                connectedService={state.connectedService}
                options={editorDisplayOptions}
                data-testid="editor-display"
              />
            )}
          </div>
        </div>
      )}
      {formDataService?.gaTag && formDataService?.gaCrossDomainTag && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${formDataService.gaTag}`}
          />
          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('set', 'linker', {'domains': [${
                  formDataService.gaCrossDomainTag.length !== 0
                    ? formDataService.gaCrossDomainTag
                        .map((x) => `'${x}'`)
                        .toString()
                    : ""
                }]});
                gtag('js', new Date());

                gtag('config', '${formDataService.gaTag}');
                `,
            }}
          />
        </>
      )}
      {formDataService?.gtmId && (
        <GoogleTagManager gtmId={formDataService.gtmId} />
      )}
      {page?.user_plan_config?.advertisement && (
        <>
          <AdvertisementFooter />
        </>
      )}
    </>
  );
};

PageIndex.getLayout = function getLayout(children: ReactElement) {
  return (
    <div className="overflow-hidden">
      <div className="min-w-full min-h-screen">{children}</div>
    </div>
  );
};

export default PageIndex;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const notFound = () => {
    return {
      notFound: true,
    };
  };

  if (!context.params || !context.params.uuid) {
    return notFound();
  }

  const uuid = String(context.params.uuid);
  try {
    const res = await pageApi.getPage({ uuid: uuid });
    const { title, image, image_width, image_height } = getOgpData(res.data);
    return {
      props: {
        uuid,
        pageData: res.data,
        serviceName: res.data.service_name,
        ogp: { title, image, image_width, image_height },
      },
    };
  } catch (e) {
    if (e instanceof ApiResponseError) {
      return notFound();
    }
  }
  return notFound();
}

const getOgpData = (page: Page) => {
  const ogpTitle = page.page_ogp?.title || "";
  const ogpDescription = page.page_ogp?.description || "";
  const ogpUrl = page.page_ogp?.url || "";
  const ogpImageWidth = page.page_ogp?.image_width || "";
  const ogpImageHeight = page.page_ogp?.image_height || "";
  let ogpImage = page.page_ogp?.image;

  if (!ogpImage) {
    // デフォルトでLPの一番上の画像が入る
    const { draftContentState } = page.page_editor_content;
    // OPTIMIZATION: Use getTemplatePartArrayFromRaw instead of creating EditorState
    // This avoids expensive EditorState creation and ContentState conversion
    const parts = getTemplatePartArrayFromRaw(draftContentState);
    const img = getDefaultOgpImage(parts);
    ogpImage = img;
  }

  return {
    title: ogpTitle,
    description: ogpDescription,
    image: ogpImage,
    image_width: ogpImageWidth,
    image_height: ogpImageHeight,
    url: ogpUrl,
  };
};
